/**
 * /activar/:token — activación de la cuenta del residente (sin sesión).
 *
 * Todo el flujo corre en Cloud Functions (functions/services/activation.js):
 *   1. getActivationContext   → resuelve el token (ok | expired | invalid).
 *   2. submitActivationCedula → valida el documento (2º factor) y decide el paso.
 *   3. completeActivation     → crea la cuenta Auth, activa la ficha, asocia la
 *                               unidad y registra el consentimiento (Ley 18.331).
 * El navegador no lee ni escribe users / apartments / userbuilding ni el
 * activationToken: el residente está deslogueado y las reglas lo rechazan (ver
 * memoria [[activacion-rompe-sin-login]]). Con el OK, el cliente inicia sesión
 * con el email y la contraseña que eligió.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { functions, auth } from "../firebase";
import { HABI_API_BASE_URL } from "../utils/habiApi";
import { logEvent } from "../utils/analytics";
import { mensajeDeError, codigoDeError } from "../utils/identityApi";
import {
  isNumericDocType,
  normalizeDocumento,
  getDocumentoValidationMessage,
} from "../utils/documento";
import { colors, radius, spacing, shadows, typography, transitions } from "../utils/designTokens";
import { MSG_REENVIO_ENVIADO, MSG_REENVIO_NO_LLEGA } from "./ActivacionExpirada";
import logo from "../assets/logo.png";

// Pantalla pública (fuera del shell de la app). Accesibilidad 30–90: texto
// ≥16px, labels ≥15px, targets ≥48px y bordes visibles. Colores, sombras y
// radios de designTokens; solo peso 400 (la jerarquía va por color y tamaño).
const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bgMain,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[6],
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: colors.bgCard,
    borderRadius: radius.lg,
    padding: spacing[8],
    boxShadow: shadows.md,
  },
  logo: { width: 120, marginBottom: spacing[6], display: "block" },
  title: { margin: `0 0 ${spacing[2]}px`, fontSize: typography.sizes["2xl"], fontWeight: 400, color: colors.black },
  subtitle: { margin: `0 0 ${spacing[6]}px`, fontSize: typography.sizes.lg, color: colors.gray700, lineHeight: 1.5 },
  enfasis: { fontWeight: 400, color: colors.black },
  muted: { margin: 0, fontSize: typography.sizes.lg, color: colors.gray600 },
  label: { display: "block", fontWeight: 400, marginBottom: spacing[2], fontSize: 15, color: colors.gray900 },
  input: {
    width: "100%",
    padding: "14px 16px",
    minHeight: 50,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.lg,
    color: colors.gray900,
    background: colors.white,
    marginBottom: spacing[4],
  },
  inputReadOnly: { background: colors.gray100, color: colors.gray600 },
  pwWrap: { position: "relative", display: "flex", alignItems: "center" },
  eyeBtn: {
    position: "absolute",
    right: 6,
    top: 3,
    height: 44,
    width: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    color: colors.gray600,
    cursor: "pointer",
    borderRadius: radius.sm,
  },
  hint: { fontSize: typography.sizes.base, color: colors.gray600, margin: `${spacing[2]}px 0 ${spacing[4]}px`, lineHeight: 1.5 },
  btn: {
    width: "100%",
    minHeight: 48,
    padding: 14,
    border: "none",
    borderRadius: radius.sm,
    background: colors.primaryDark,
    color: colors.white,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  btnBusy: { opacity: 0.7, cursor: "wait" },
  error: { color: colors.errorDark, fontSize: 15, marginTop: spacing[2], lineHeight: 1.5 },
  success: {
    padding: spacing[5],
    background: colors.successLight,
    borderRadius: radius.md,
    color: colors.successDark,
    fontWeight: 400,
    marginTop: spacing[4],
  },
  resendOk: { color: colors.successDark, fontSize: 15, lineHeight: 1.5, margin: `${spacing[2]}px 0 0` },
  linkInvalid: {
    padding: spacing[6],
    textAlign: "center",
    color: colors.gray600,
    fontSize: 15,
  },
  resumen: {
    marginBottom: spacing[5],
    padding: spacing[4],
    background: colors.gray50,
    borderRadius: radius.md,
    border: `1px solid ${colors.gray200}`,
  },
  resumenFila: { marginBottom: spacing[2] },
  resumenLabel: { fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray600 },
  resumenValor: { fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black },
};

const btnStyle = (busy) => (busy ? { ...styles.btn, ...styles.btnBusy } : styles.btn);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MSG_DOCUMENTO_NO_COINCIDE =
  "El número de documento ingresado no coincide con la invitación. Verificá el número e intentá de nuevo, o contactá a tu administrador.";
const MSG_CUENTA_YA_EXISTE =
  "Ya tenés una cuenta con ese email. Ingresá con tu contraseña en la pantalla de acceso; si no la recordás, usá 'Olvidé mi contraseña'.";

// Las tres funciones de activación son públicas: el residente no tiene sesión.
const llamarActivacion = async (nombre, data) => {
  const res = await httpsCallable(functions, nombre)(data);
  return res.data;
};

// Mensaje del servidor (en español) o, si no lo hay, el de respaldo. Sin
// mensaje propio (caída de red, excepción no controlada, timeout del
// navegador) el SDK manda el código como texto: "internal", "INTERNAL",
// "deadline-exceeded"; eso no se le muestra al residente.
const mensajeActivacion = (err, fallback) => {
  if (!String(err?.code || "").startsWith("functions/")) return fallback;
  const msg = mensajeDeError(err, fallback);
  return msg.toLowerCase().replace(/_/g, "-") === codigoDeError(err) ? fallback : msg;
};

const ActivarCuenta = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // Datos de display de la invitación, sin el documento:
  // { id, userid, email, NombreCompleto, name, ApellidosCompleto, tipoDocumento, celular }.
  const [user, setUser] = useState(null);
  const [step, setStep] = useState("cedula"); // "cedula" | "confirmar_datos" | "ya_asociado" | "cuenta_existente" | "ok"
  // El residente CREA su contraseña (la cuenta Auth) en el paso confirmar_datos.
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  // Link vencido: guardamos el email del token (aunque venció) para ofrecer
  // reenviar uno nuevo con un solo toque, sin re-tipear el email.
  const [expiredEmail, setExpiredEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [cedula, setCedula] = useState("");
  const [formConfirmar, setFormConfirmar] = useState({ email: "", password: "", confirmPassword: "" });
  const [showPass, setShowPass] = useState(false); // mostrar/ocultar contraseña (accesibilidad 30–90)
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Contexto "público" de la invitación (unidad, edificio, rol), sin ids.
  const [invitationContext, setInvitationContext] = useState(null);
  const [asociadoOk, setAsociadoOk] = useState(false);
  const [existingMaskedEmail, setExistingMaskedEmail] = useState("");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let vigente = true;
    const cargar = async () => {
      try {
        const data = await llamarActivacion("getActivationContext", { token });
        if (!vigente) return;
        if (data?.status === "ok") {
          setUser(data.user);
          logEvent("activation_link_opened", { token: true });
        } else if (data?.status === "expired") {
          setExpiredEmail(String(data.expiredEmail || "").trim());
          setUser(null);
        } else {
          setUser(null);
        }
      } catch (e) {
        console.error("getActivationContext falló:", e);
        if (vigente) setUser(null);
      } finally {
        if (vigente) setLoading(false);
      }
    };
    cargar();
    return () => {
      vigente = false;
    };
  }, [token]);

  // Mapea la decisión del servidor (submitActivationCedula) a los pasos de la
  // pantalla.
  const applyCedulaDecision = (data) => {
    switch (data?.decision) {
      case "confirmar_datos":
        setInvitationContext(data.invitationContext || null);
        setFormConfirmar({ email: data.email || "", password: "", confirmPassword: "" });
        setNeedsPasswordSetup(!!data.needsPasswordSetup);
        setStep("confirmar_datos");
        break;
      case "ya_asociado":
        setInvitationContext(data.invitationContext || null);
        setStep("ya_asociado");
        break;
      case "asociado_ok":
        setInvitationContext(data.invitationContext || null);
        setAsociadoOk(true);
        setStep("ok");
        break;
      case "cuenta_existente":
        setInvitationContext(data.invitationContext || null);
        setExistingMaskedEmail(data.maskedEmail || "");
        setStep("cuenta_existente");
        break;
      case "mismatch":
        setError(MSG_DOCUMENTO_NO_COINCIDE);
        break;
      case "expired":
      case "invalid":
        setUser(null);
        break;
      default:
        setError(data?.message || "No se pudo verificar el documento. Reintentá.");
    }
  };

  const handleCedulaSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    const tipoDoc = user?.tipoDocumento || "CI";
    const cedulaIngresada = String(cedula ?? "").trim();
    const docError = getDocumentoValidationMessage(tipoDoc, cedulaIngresada);
    if (docError) {
      setError(docError);
      return;
    }
    setSaving(true);
    try {
      const data = await llamarActivacion("submitActivationCedula", { token, cedula: cedulaIngresada });
      applyCedulaDecision(data);
    } catch (err) {
      console.error("submitActivationCedula falló:", err);
      setError(mensajeActivacion(err, "No se pudo verificar el documento. Reintentá."));
    } finally {
      setSaving(false);
    }
  };

  // Reenvío self-service del link de activación cuando venció, por el canal
  // confiable (habi@habitia.ai). Un solo toque: usa el email del token vencido,
  // tal cual está en la ficha. El servidor responde "sent" haya o no algo que
  // mandar (no deja averiguar qué emails tienen cuenta), así que el texto es
  // uno solo, el mismo de /activar/expirado; "already_active" lo devolvía el
  // backend anterior.
  const handleResendActivation = async () => {
    if (!expiredEmail) {
      navigate("/activar/expirado");
      return;
    }
    setResendLoading(true);
    setResendStatus("");
    try {
      const resp = await fetch(`${HABI_API_BASE_URL}/api/resend-activation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: expiredEmail }),
      });
      const data = resp.ok ? await resp.json() : {};
      if (data.status === "sent" || data.status === "already_active") {
        setResendStatus(MSG_REENVIO_ENVIADO);
      } else {
        navigate("/activar/expirado"); // fallback (email inválido, error o servicio caído)
      }
    } catch (e) {
      console.warn("resend-activation no disponible, uso fallback:", e);
      navigate("/activar/expirado");
    } finally {
      setResendLoading(false);
    }
  };

  // Crea la cuenta + activa + asocia en el servidor (Admin SDK) y después
  // inicia sesión para que entre directo a la app.
  const handleConfirmarDatosSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    const email = (formConfirmar.email || "").trim();
    if (!email) {
      setError("Ingresá tu email.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError("Email inválido.");
      return;
    }
    if (needsPasswordSetup) {
      if (!formConfirmar.password || formConfirmar.password.length < 6) {
        setError("La contraseña debe tener al menos 6 caracteres.");
        return;
      }
      if (formConfirmar.password !== formConfirmar.confirmPassword) {
        setError("Las contraseñas no coinciden.");
        return;
      }
    }
    setSaving(true);
    try {
      const data = await llamarActivacion("completeActivation", {
        token,
        cedula,
        email,
        password: formConfirmar.password,
      });
      if (data?.status === "ok" || data?.status === "already_active") {
        logEvent("password_created", { via: "activacion" });
        logEvent("activation_completed", { via: "password" });
        try {
          await signInWithEmailAndPassword(auth, data.email || email, formConfirmar.password);
        } catch (signErr) {
          // Si el auto-login falla, la pantalla "ok" le dice que ingrese manualmente.
          console.warn("Auto-login tras activar no disponible:", signErr);
        }
        setStep("ok");
      } else {
        setError("No se pudo completar. Reintentá más tarde.");
      }
    } catch (err) {
      console.error("completeActivation falló:", err);
      setError(
        codigoDeError(err) === "already-exists"
          ? MSG_CUENTA_YA_EXISTE
          : mensajeActivacion(err, "No se pudo completar. Reintentá más tarde.")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <p style={styles.muted}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <h1 style={styles.title}>Tu link expiró</h1>
          {resendStatus ? (
            <>
              <p style={styles.resendOk} role="status">{resendStatus}</p>
              <p style={{ ...styles.muted, marginTop: spacing[3], lineHeight: 1.5 }}>{MSG_REENVIO_NO_LLEGA}</p>
            </>
          ) : (
            <>
              <p style={styles.linkInvalid}>
                Los links de activación vencen a las 48 horas por seguridad.
                {expiredEmail
                  ? " Te mandamos uno nuevo a tu email con un solo toque:"
                  : " Pedí uno nuevo desde la siguiente pantalla; te lo mandamos por email."}
              </p>
              <button
                type="button"
                className="habitia-btn-primary"
                style={btnStyle(resendLoading)}
                onClick={handleResendActivation}
                disabled={resendLoading}
              >
                {resendLoading ? "Enviando…" : expiredEmail ? "Enviarme un link nuevo" : "Pedir nuevo link"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (step === "ok") {
    // A "ok" se llega por dos caminos: la cuenta ya existía y faltaba asociar la
    // unidad (asociado_ok) o el residente acaba de crear su cuenta.
    const okTitle = asociadoOk ? "Asociado correctamente" : "Cuenta creada";
    const okSubtitle = asociadoOk
      ? "Tu usuario quedó asociado a la unidad de la invitación."
      : "Tu cuenta en Habitia quedó creada. Podés ingresar con tu email y contraseña.";
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <h1 style={styles.title}>{okTitle}</h1>
          <p style={styles.subtitle}>{okSubtitle}</p>
          <div style={styles.success}>Podés ingresar a la plataforma con tu usuario.</div>
        </div>
      </div>
    );
  }

  if (step === "confirmar_datos" && invitationContext) {
    const nombrePrecargado = user?.NombreCompleto || user?.name || "—";
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <h1 style={styles.title}>Creá tu contraseña</h1>
          <p style={styles.subtitle}>
            Confirmá tu email y elegí una contraseña para terminar de activar tu cuenta y poder ingresar.
          </p>
          <div style={styles.resumen}>
            <div style={styles.resumenFila}>
              <span style={styles.resumenLabel}>Nombre</span>
              <div style={styles.resumenValor}>{nombrePrecargado}</div>
            </div>
            <div style={styles.resumenFila}>
              {/* Con varias unidades en el edificio, la cuenta da acceso a todas. */}
              <span style={styles.resumenLabel}>{invitationContext.unitCount > 1 ? "Unidades" : "Unidad"}</span>
              <div style={styles.resumenValor}>
                {invitationContext.unitCount > 1 ? invitationContext.apartmentNumber : invitationContext.unitLabel}
              </div>
            </div>
            <div style={styles.resumenFila}>
              <span style={styles.resumenLabel}>Rol</span>
              <div style={styles.resumenValor}>{invitationContext.rol}</div>
            </div>
          </div>
          <form onSubmit={handleConfirmarDatosSubmit}>
            <label htmlFor="confirmar-email" style={styles.label}>Email *</label>
            <input
              id="confirmar-email"
              type="email"
              style={user?.email ? { ...styles.input, ...styles.inputReadOnly } : styles.input}
              value={formConfirmar.email}
              onChange={(e) => setFormConfirmar((f) => ({ ...f, email: e.target.value }))}
              placeholder="tu@email.com"
              readOnly={!!user?.email}
              required
            />
            {needsPasswordSetup && (
              <>
                <label htmlFor="confirmar-password" style={styles.label}>Elegí tu contraseña *</label>
                <div style={styles.pwWrap}>
                  <input
                    id="confirmar-password"
                    type={showPass ? "text" : "password"}
                    style={{ ...styles.input, paddingRight: 48, marginBottom: 0 }}
                    value={formConfirmar.password}
                    onChange={(e) => setFormConfirmar((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    aria-pressed={showPass}
                    aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                    style={styles.eyeBtn}
                  >
                    {showPass ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
                <div style={styles.hint}>
                  Mínimo 6 caracteres. Tocá el ojo para ver lo que escribís.
                </div>
                <label htmlFor="confirmar-password-confirm" style={styles.label}>Repetí la contraseña *</label>
                <input
                  id="confirmar-password-confirm"
                  type={showPass ? "text" : "password"}
                  style={styles.input}
                  value={formConfirmar.confirmPassword}
                  onChange={(e) => setFormConfirmar((f) => ({ ...f, confirmPassword: e.target.value }))}
                  placeholder="Repetí la contraseña"
                  minLength={6}
                  autoComplete="new-password"
                />
              </>
            )}
            {error && <div style={styles.error} role="alert">{error}</div>}
            <button type="submit" className="habitia-btn-primary" style={btnStyle(saving)} disabled={saving}>
              {saving ? "Creando..." : "Crear"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === "cuenta_existente") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <h1 style={styles.title}>Ya tenés una cuenta</h1>
          <p style={styles.subtitle}>
            Ese documento ya tiene una cuenta en Habitia{existingMaskedEmail ? ` (${existingMaskedEmail})` : ""}. Entrá con tu email y contraseña; si no la recordás, usá “Olvidé mi contraseña”.
          </p>
          <p style={{ ...styles.subtitle, fontSize: 15 }}>
            Si creés que es un error, escribile a la administración de tu edificio.
          </p>
          <button type="button" className="habitia-btn-primary" style={styles.btn} onClick={() => navigate("/")}>
            Ir a ingresar
          </button>
        </div>
      </div>
    );
  }

  if (step === "ya_asociado" && invitationContext) {
    const edificioNombre = invitationContext.buildingName || "este edificio";
    const apartamentoNombre = invitationContext.apartmentNumber ?? invitationContext.unitLabel ?? "esta unidad";
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logo} alt="Habitia" style={styles.logo} />
          <h1 style={styles.title}>Usuario ya asociado</h1>
          <p style={{ ...styles.subtitle, marginBottom: 0 }}>
            Su usuario está creado y asociado al edificio <strong style={styles.enfasis}>{edificioNombre}</strong> y{" "}
            {invitationContext.unitCount > 1 ? "apartamentos" : "apartamento"} <strong style={styles.enfasis}>{apartamentoNombre}</strong>. Gracias por utilizar Habitia.
          </p>
        </div>
      </div>
    );
  }

  const tipoDoc = user?.tipoDocumento || "CI";
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src={logo} alt="Habitia" style={styles.logo} />
        <h1 style={styles.title}>Activación de cuenta</h1>
        <p style={styles.subtitle}>
          La comisión de tu edificio te invitó a usar Habitia. Ingresá tu documento de identidad para activar tu cuenta.
        </p>
        <form onSubmit={handleCedulaSubmit}>
          <label htmlFor="activar-cedula" style={styles.label}>Documento de identidad</label>
          <input
            id="activar-cedula"
            style={styles.input}
            value={cedula}
            onChange={(e) => setCedula(normalizeDocumento(e.target.value, tipoDoc))}
            placeholder={isNumericDocType(tipoDoc) ? "Ej: 12345678 (sin puntos ni guiones)" : "Ej: AB123456 (letras y números)"}
            autoFocus
            inputMode={isNumericDocType(tipoDoc) ? "numeric" : "text"}
          />
          {error && <div style={styles.error} role="alert">{error}</div>}
          <button type="submit" className="habitia-btn-primary" style={btnStyle(saving)} disabled={saving}>
            {saving ? "Verificando..." : "Continuar"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ActivarCuenta;
